import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { sugarData, sugarCategoryLabels } from '../data/sugarData';
import { Search, Filter, ChevronRight, Candy } from 'lucide-react';

// AI-Generated images for sugar products
const sugarImages = {
  'icumsa-45': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/66445efc0d828bce8a60cb23e72e1e2edffe3626719afecc490acb033d435a63.png',
  'icumsa-100': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/525eeb5bb71ce4020d705669c9300b8f4e945ff8c8bf965cb790c014146dc2fe.png',
  'icumsa-150': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/aac68068db0f5ec885e7a4cf4ae77df7c722c7dce317f869971a47252a855407.png',
  'raw-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/58483224c19433396882d43eb91cf1a7d4018dbfdf9167dfbf572a7cfc861bad.png',
  'plantation-white': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/7e3d08efecd864348a9231d4fe4580c30c9a158c59b13376115432798ba9cd96.png',
  'brown-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/7a94b6fc85c9f33323cbef4c807b56ea19fd4fbf8f24394db6b07e143e2d1f14.png',
  'demerara-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/539ab2a2665b27a164286a742e5da28700b9babc99814e2af40da36b84b9fd59.png',
  'muscovado-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/16988f91e249e90c10938aa004b4fad88a79a09f994dec76e169e89ca9786744.png',
  'jaggery-powder': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/05daff6cee1591796f76cd955d15666f5bd09505af3b17ed90781c6bf9f12046.png',
  'jaggery-blocks': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/0c903422c216c083f4f59ca97e63a7821df40f2073d74b168f9cf35294468e67.png',
  'palm-jaggery': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/2e3c823fc37b03eaca959c89a132199b901d933918702601e12adb49d6ca562b.png',
  'caster-sugar': 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/3fcd6d393195f2fcfd93710bd0c74442c88b3f56c7bde5b4d8d1b03b490bbba7.png',
};

const getSugarImage = (imageKey) => {
  return sugarImages[imageKey] || 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=300&fit=crop';
};

export default function SugarCatalogue() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredSugars = useMemo(() => {
    return sugarData.filter(sugar => {
      const matchesSearch = sugar.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           sugar.grade.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           sugar.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || sugar.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const categories = useMemo(() => {
    const cats = [...new Set(sugarData.map(s => s.category))];
    return cats.sort((a, b) => sugarCategoryLabels[a].localeCompare(sugarCategoryLabels[b]));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#263776] via-[#1e2d5f] to-[#263776] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#F47D20] rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F47D20] rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 py-16 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm mb-6 text-gray-300">
            <Link to="/" className="hover:text-[#F47D20] transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/products" className="hover:text-[#F47D20] transition-colors">Products</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#F47D20]">Sugar</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#F47D20]/20 rounded-xl flex items-center justify-center">
              <Candy className="w-7 h-7 text-[#F47D20]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">Sugar</h1>
          </div>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mb-8">
            India is one of the world's largest sugar producers and exporters. Explore our comprehensive catalogue 
            featuring refined sugars, raw sugars, specialty varieties, and traditional jaggery products.
          </p>
          
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search sugars..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F47D20] focus:border-transparent"
                data-testid="sugar-search-input"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full sm:w-auto pl-12 pr-8 py-3 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#F47D20] focus:border-transparent appearance-none cursor-pointer"
                data-testid="sugar-category-filter"
              >
                <option value="all" className="bg-[#263776]">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat} className="bg-[#263776]">
                    {sugarCategoryLabels[cat]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Sugar Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-[#263776]">{filteredSugars.length}</span> products
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredSugars.map((sugar) => (
            <Link
              key={sugar.id}
              to={`/products/sugar/${sugar.id}`}
              className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              data-testid={`sugar-card-${sugar.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={getSugarImage(sugar.image)}
                  alt={sugar.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="absolute top-3 right-3 px-2 py-1 bg-[#F47D20] text-white text-xs font-medium rounded-full">
                  {sugarCategoryLabels[sugar.category]}
                </span>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-[#263776] group-hover:text-[#F47D20] transition-colors mb-1">
                  {sugar.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2">{sugar.grade}</p>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {sugar.description.substring(0, 100)}...
                </p>
                <div className="mt-3 flex items-center text-[#F47D20] text-sm font-medium">
                  View Details
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredSugars.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-[#263776] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Premium Quality Sugar</h2>
            <p className="text-gray-300 mb-8">
              SPANXPORT sources sugar directly from certified mills and refineries across India's sugar belt.
              Our rigorous quality control ensures compliance with international standards including ICUMSA specifications.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#F47D20] mb-2">12+</div>
                <div className="text-sm text-gray-300">Sugar Varieties</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#F47D20] mb-2">ICUMSA</div>
                <div className="text-sm text-gray-300">Certified</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#F47D20] mb-2">Bulk</div>
                <div className="text-sm text-gray-300">Shipments</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#F47D20] mb-2">ISO</div>
                <div className="text-sm text-gray-300">22000 Ready</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-[#F47D20] to-[#ff9a4d] rounded-2xl p-8 md:p-12 text-white text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Need Bulk Sugar?</h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Contact our team for competitive pricing on bulk orders, specific ICUMSA requirements, 
            and tailored packaging solutions for your business needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-3 bg-white text-[#F47D20] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="sugar-catalogue-get-quote-btn"
          >
            Get a Quote
            <ChevronRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
}
